import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const devopsArtwork = '/assets/illustrations/devops-orbit.svg';

const DevOps = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const features = [
    {
      icon: 'pipeline',
      title: 'CI/CD Pipelines',
      subtitle: 'Automation',
      description: 'Automated build, test, and deployment pipelines that reduce release time from hours to minutes while keeping quality gates enforceable.'
    },
    {
      icon: 'infrastructure',
      title: 'Infrastructure as Code',
      subtitle: 'IaC Blueprints',
      description: 'Version-controlled infrastructure provisioning using Terraform, CloudFormation, and Ansible for consistent, auditable environments.'
    },
    {
      icon: 'monitor',
      title: 'Monitoring & Observability',
      subtitle: 'Real-time Insights',
      description: 'Unified telemetry with Prometheus, Grafana, and ELK for proactive issue detection, SLO tracking, and executive-ready reporting.'
    },
    {
      icon: 'container',
      title: 'Container Orchestration',
      subtitle: 'Kubernetes',
      description: 'Docker containerisation and Kubernetes orchestration for scalable, portable applications with policy-driven resource controls.'
    },
    {
      icon: 'shield',
      title: 'Security Integration',
      subtitle: 'DevSecOps',
      description: 'Shift-left security with automated scanning, compliance evidence capture, and guardrails aligned to regulatory baselines.'
    },
    {
      icon: 'performance',
      title: 'Performance Optimisation',
      subtitle: 'Efficiency',
      description: 'Continuous profiling, chaos drills, and capacity planning to keep experiences fast and resilient under any load.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Assessment & Planning',
      description: 'We analyse your delivery workflow, identify bottlenecks, and define the operating model, success metrics, and transformation roadmap.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Tool Selection & Setup',
      description: 'Co-select and configure the right tooling across SCM, CI/CD, testing, security, and observability, aligned to governance expectations.',
      icon: 'toolkit'
    },
    {
      step: '03',
      title: 'Pipeline Implementation',
      description: 'Implement automated pipelines with gated testing, security scans, and progressive deployment patterns such as blue/green and canary.',
      icon: 'pipeline'
    },
    {
      step: '04',
      title: 'Infrastructure Automation',
      description: 'Deploy infrastructure-as-code modules, drift controls, and policy enforcement for consistent provisioning across environments.',
      icon: 'automation'
    },
    {
      step: '05',
      title: 'Monitoring & Optimization',
      description: 'Launch observability, alert orchestration, and optimisation cadences to ensure continuous improvement and measurable outcomes.',
      icon: 'analytics'
    }
  ];

  const technologies = [
    {
      name: 'Jenkins',
      tagline: 'CI Orchestration',
      description: 'Enterprise pipelines with shared libraries, approvals, and compliance automation.',
      icon: 'automation'
    },
    {
      name: 'GitLab CI',
      tagline: 'Unified DevSecOps',
      description: 'Integrated SCM, CI/CD, and security scanning with self-hosted or SaaS models.',
      icon: 'pipeline'
    },
    {
      name: 'GitHub Actions',
      tagline: 'Workflow Automation',
      description: 'Event-driven workflows powering build, test, release, and policy checks across repos.',
      icon: 'toolkit'
    },
    {
      name: 'CircleCI',
      tagline: 'Cloud-native CI',
      description: 'Parallel execution, insights, and reusable orbs to accelerate delivery teams.',
      icon: 'automation'
    },
    {
      name: 'Argo CD & Rollouts',
      tagline: 'GitOps Delivery',
      description: 'Declarative Kubernetes deployment with canary, blue/green, and progressive delivery.',
      icon: 'rocket'
    },
    {
      name: 'Spinnaker',
      tagline: 'Multi-Cloud CD',
      description: 'Automated release orchestration with policy gates across AWS, GCP, and Kubernetes.',
      icon: 'multiCloud'
    },
    {
      name: 'Docker & Helm',
      tagline: 'Container Supply Chain',
      description: 'Standardised images and chart packaging to promote repeatable application delivery.',
      icon: 'container'
    },
    {
      name: 'Kubernetes',
      tagline: 'Cluster Management',
      description: 'Self-healing workloads, autoscaling, and policy frameworks for hybrid fleets.',
      icon: 'multiCloud'
    },
    {
      name: 'Terraform & Terragrunt',
      tagline: 'Infrastructure as Code',
      description: 'Modular, version-controlled provisioning with environment drift detection and policy guardrails.',
      icon: 'infrastructure'
    },
    {
      name: 'Ansible & Chef',
      tagline: 'Configuration Automation',
      description: 'Idempotent configuration, golden images, and compliance as code for servers and network.',
      icon: 'api'
    },
    {
      name: 'HashiCorp Vault',
      tagline: 'Secrets Management',
      description: 'Dynamic secrets, PKI automation, and zero-trust credential brokering across platforms.',
      icon: 'shield'
    },
    {
      name: 'Prometheus & Grafana',
      tagline: 'Observability',
      description: 'Metrics, alerting, and visualisations powering SLO management and operational dashboards.',
      icon: 'monitor'
    },
    {
      name: 'ELK & OpenSearch',
      tagline: 'Log Analytics',
      description: 'Centralised logging, tracing, and search to accelerate RCA and security investigations.',
      icon: 'search'
    },
    {
      name: 'Datadog',
      tagline: 'Full-stack Monitoring',
      description: 'APM, RUM, infrastructure, and security analytics correlated for proactive incident response.',
      icon: 'insight'
    },
    {
      name: 'AWS DevOps & CodeSuite',
      tagline: 'Public Cloud Tooling',
      description: 'Native build, deploy, and operations services integrated with IAM and CloudWatch.',
      icon: 'cloud'
    },
    {
      name: 'Azure DevOps & GitHub Enterprise',
      tagline: 'Enterprise Platforms',
      description: 'Work item tracking, release pipelines, and governance for Microsoft ecosystems.',
      icon: 'cloudUpload'
    }
  ];

  const marqueeRows = [
    technologies.filter((_, index) => index % 2 === 0),
    technologies.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  return (
    <motion.div 
      className="service-page devops-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Hero Section */}
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={devopsArtwork} 
                alt="Abstract illustration representing DevOps automation" 
              />
            </motion.div>
            <h1>DevOps Solutions</h1>
            <p className="hero-description">
              Accelerate product delivery with platform engineering squads that embed automation, IaC, and observability into every release. Our DevOps operating model creates a unified toolchain, measurable service levels, and a culture of continuous improvement.
            </p>
            <Link to="/contact" className="btn">Start Your DevOps Journey</Link>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              Advisory architects, platform engineers, and SRE specialists align to modernise your software factory. We assess tooling, redefine ways of working, and sequence automation backlogs so every squad ships faster without compromising security or reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={feature.icon} size={72} backgroundOpacity={0.24} />
                </div>
                {feature.subtitle && (
                  <div className="feature-subtitle">{feature.subtitle}</div>
                )}
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our DevOps Process</h2>
            <p>A structured approach to transforming your development and deployment workflows</p>
          </motion.div>
          
          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Technologies We Use</h2>
            <p>Industry-leading tools and platforms for robust DevOps implementations</p>
          </motion.div>
          
          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`devops-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((tech, index) => (
                    <div className="tech-marquee__card" key={`devops-tech-${rowIndex}-${index}-${tech.name}`}>
                      <IconBadge
                        name={tech.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{tech.name}</span>
                        {tech.tagline && (
                          <span className="tech-marquee__subtitle">{tech.tagline}</span>
                        )}
                        {tech.description && (
                          <span className="tech-marquee__description">{tech.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default DevOps;
